<template>
	<div class="content sections_dashboard sections_analytics">

		<h2>Pregled izvajanja ukrepov v letu {{ year }}</h2>

		<div class="analytics_faculty_select">
			<label for="id_faculty_select">Fakulteta: </label>
			<select id="id_faculty_select" v-model="id_faculty_select" @change="loadAnalytics">
				<option v-for="faculty in getSortedFacultiesByAcronym" v-bind:value="faculty.id" v-bind:key="faculty.id">
					{{ faculty.acronym }}
				</option>
			</select>
		</div>

		<div v-if="sectionsAnalytics && sectionsAnalytics.sections && sectionsAnalytics.sections.length === 0" class="dashboard_no_data">
			Ni podatkov za izbrano fakulteto.
		</div>

		<div v-else-if="sectionsAnalytics" class="analytics_sections_list">
			<div class="analytics_section_block analytics_general_block">
				<div
					class="expandable-table-header"
					:class="{ expanded: showGeneralData }"
					@click="showGeneralData = !showGeneralData"
				>
					<div class="header-content">
						<div class="header-icon">
							<font-awesome-icon
								v-if="!showGeneralData"
								:icon="['fas', 'chevron-down']"
								title="Razširi pogled"
							/>
							<font-awesome-icon v-else :icon="['fas', 'chevron-up']" title="Skrči pogled" />
						</div>
						<div class="header-text">
							<span class="header-title">Splošni podatki</span>
							<span class="header-subtitle">
								{{ generalSubsectionsSummary.length }}
								{{ generalSubsectionsSummary.length === 1 ? 'podpodročje' : 'podpodročij' }}
								· Kliknite za prikaz podatkov
							</span>
						</div>
						<div class="header-indicator">
							<font-awesome-icon v-if="!showGeneralData" :icon="['fas', 'angle-down']" />
							<font-awesome-icon v-else :icon="['fas', 'angle-up']" />
						</div>
					</div>
				</div>

				<div v-show="showGeneralData" class="table_content_work_plan analytics_section_content">
					<div class="analytics_table_wrap table_static_data analytics_summary_table">
						<table>
							<thead>
								<tr>
									<th><div class="name">Področje</div></th>
									<th><div class="name">Podpodročje</div></th>
									<th class="analytics_count_col"><div class="name">Število vrstic</div></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="row in generalSubsectionsSummary" :key="row.key">
									<td>{{ row.section_name }}</td>
									<td>
										{{ row.subsection_name }}
										<span v-if="row.level_name" class="analytics_level">({{ row.level_name }})</span>
									</td>
									<td class="analytics_count_col">{{ row.row_count }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<div class="analytics_section_block" v-for="section in orderedSections" :key="section.section_id">
				<div
					class="expandable-table-header"
					:class="{ expanded: isSectionExpanded(section.section_id) }"
					@click="toggleSection(section.section_id)"
				>
					<div class="header-content">
						<div class="header-icon">
							<font-awesome-icon
								v-if="!isSectionExpanded(section.section_id)"
								:icon="['fas', 'chevron-down']"
								title="Razširi pogled"
							/>
							<font-awesome-icon v-else :icon="['fas', 'chevron-up']" title="Skrči pogled" />
						</div>
						<div class="header-text">
							<span class="header-title">{{ section.section_name }}</span>
							<span class="header-subtitle">
								{{ section.subsections.length }}
								{{ section.subsections.length === 1 ? 'podpodročje' : 'podpodročij' }}
								· Kliknite za prikaz podatkov
							</span>
						</div>
						<div class="header-indicator">
							<font-awesome-icon
								v-if="!isSectionExpanded(section.section_id)"
								:icon="['fas', 'angle-down']"
							/>
							<font-awesome-icon v-else :icon="['fas', 'angle-up']" />
						</div>
					</div>
				</div>

				<div v-show="isSectionExpanded(section.section_id)" class="table_content_work_plan analytics_section_content">
					<div class="analytics_subsection" v-for="subsection in section.subsections" :key="subsection.subsection_id">
						<h4>
							{{ subsection.subsection_name }}
							<span v-if="subsection.level_name" class="analytics_level">({{ subsection.level_name }})</span>
						</h4>

						<p class="analytics_row_count">Število vrstic: <strong>{{ subsection.row_count }}</strong></p>

						<div class="analytics_column" v-for="column in subsection.columns" :key="column.column_id">
							<p class="analytics_column_name">
								{{ column.column_name }}
								<span class="analytics_column_type">({{ columnTypeLabel(column.type) }})</span>
							</p>

							<div class="analytics_column_row">
								<div class="analytics_table_wrap table_static_data">
									<table>
										<thead>
											<tr>
												<th>
													<div class="name">Vrednost</div>
												</th>
												<th class="analytics_count_col">
													<div class="name">Število</div>
												</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="item in orderedColumnCounts(column)" :key="item.label">
												<td>{{ item.label }}</td>
												<td class="analytics_count_col">{{ item.count }}</td>
											</tr>
										</tbody>
									</table>
								</div>

								<div class="analytics_chart_wrap" v-if="shouldShowPieChart(column)">
									<SectionsAnalyticsPieChart
										:labels="pieChartLabels(column)"
										:values="pieChartValues(column)"
										:title="column.column_name"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>


<script>
import router from '../../router/router.js';
import { mapActions } from 'vuex';
import SectionsAnalyticsPieChart from './SectionsAnalyticsPieChart.vue';

export default {
	name: 'sectionsAnalytics',
	components: {
		SectionsAnalyticsPieChart,
	},
	data: function () {
		return {
			id_faculty_select: null,
			expandedSections: {},
			showGeneralData: false,
		}
	},
	computed: {
		year() {
			return this.$store.getters.year;
		},

		getSortedFacultiesByAcronym() {
			return this.$store.getters.getSortedFacultiesByAcronym;
		},

		getFaculty() {
			return this.$store.getters.getFaculty;
		},

		sectionsAnalytics() {
			return this.$store.getters.sections_analytics;
		},

		orderedSections() {
			if (!this.sectionsAnalytics?.sections) {
				return [];
			}

			const sections = [...this.sectionsAnalytics.sections];
			const indicatorIndex = sections.findIndex((section) => this.isIndicatorSection(section));

			if (indicatorIndex === -1) {
				return sections;
			}

			const indicatorSection = sections.splice(indicatorIndex, 1)[0];
			sections.push(indicatorSection);

			return sections;
		},

		generalSubsectionsSummary() {
			const rows = [];

			for (const section of this.orderedSections) {
				for (const subsection of section.subsections) {
					rows.push({
						key: `${section.section_id}-${subsection.subsection_id}`,
						section_name: section.section_name,
						subsection_name: subsection.subsection_name,
						level_name: subsection.level_name,
						row_count: subsection.row_count,
					});
				}
			}

			return rows;
		},

		getTabCode() {
			if (router.currentRoute.value?.matched?.[0]?.path == '/program-dela/' || router.currentRoute.value?.matched?.[0]?.path == '/program-dela')
				return 1;
			return 0;
		},
	},
	watch: {
		year() {
			this.loadAnalytics();
		},

		sectionsAnalytics() {
			this.expandedSections = {};
			this.showGeneralData = false;
		},
	},
	methods: {
		...mapActions([
			'getSectionsAnalytics',
		]),

		loadAnalytics() {
			if (this.id_faculty_select == null || this.getTabCode !== 0) {
				return;
			}

			this.getSectionsAnalytics({
				faculty_id: this.id_faculty_select,
				year: this.year,
				tab: 0,
			});
		},

		isSectionExpanded(sectionId) {
			return !!this.expandedSections[sectionId];
		},

		toggleSection(sectionId) {
			this.expandedSections = {
				...this.expandedSections,
				[sectionId]: !this.expandedSections[sectionId],
			};
		},

		isIndicatorSection(section) {
			return section.section_name?.trim().toLowerCase() === 'podatki za kazalnike';
		},

		columnTypeLabel(type) {
			if (type == 6) return 'Roleta';
			if (type == 7) return 'Izvorno leto vrstice';
			return '';
		},

		orderedColumnCounts(column) {
			const counts = column.counts || {};
			const items = [];
			const emptyLabel = '(prazno)';

			if (column.type == 6 && column.options) {
				for (const option of column.options) {
					items.push({ label: option, count: counts[option] || 0 });
				}
				items.push({ label: emptyLabel, count: counts[emptyLabel] || 0 });

				for (const label in counts) {
					if (label === emptyLabel || column.options.includes(label)) {
						continue;
					}
					items.push({ label: label, count: counts[label] });
				}

				return items;
			}

			const labels = Object.keys(counts).sort((a, b) => {
				if (a === emptyLabel) return -1;
				if (b === emptyLabel) return 1;
				return a.localeCompare(b, 'sl');
			});

			for (const label of labels) {
				items.push({ label: label, count: counts[label] });
			}

			return items;
		},

		nonZeroColumnCounts(column) {
			return this.orderedColumnCounts(column).filter((item) => item.count > 0);
		},

		shouldShowPieChart(column) {
			return this.nonZeroColumnCounts(column).length > 1;
		},

		pieChartLabels(column) {
			return this.nonZeroColumnCounts(column).map((item) => item.label);
		},

		pieChartValues(column) {
			return this.nonZeroColumnCounts(column).map((item) => item.count);
		},
	},
	created() {
		this.id_faculty_select = this.getFaculty.id;
		this.loadAnalytics();
	},
}
</script>


<style lang="scss" scoped>
.analytics_faculty_select {
	margin-bottom: 30px;
}

.analytics_sections_list {
	padding: 0 10px;
}

.analytics_section_block {
	margin-bottom: 16px;
}

.analytics_general_block {
	margin-bottom: 24px;
}

.analytics_section_content {
	margin-top: 0;
	padding: 0 4px 20px;
}

.analytics_subsection {
	margin-bottom: 28px;
	padding-bottom: 20px;
	border-bottom: 1px dashed #ccc;

	&:last-child {
		border-bottom: none;
		margin-bottom: 0;
		padding-bottom: 0;
	}

	h4 {
		font-size: 18px;
		margin-bottom: 8px;
		text-align: left;
	}
}

.analytics_level,
.analytics_column_type {
	font-weight: normal;
	color: #666;
	font-size: 14px;
}

.analytics_row_count {
	text-align: left;
	margin-bottom: 15px;
}

.analytics_column {
	margin-bottom: 24px;

	.analytics_column_name {
		text-align: left;
		font-size: 16px;
		margin-bottom: 10px;
	}
}

.analytics_column_row {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: 20px;
}

.analytics_table_wrap {
	flex: 0 0 auto;
	width: 40%;

	table {
		width: 100%;
	}

	tbody tr {
		transition: background-color 0.15s ease;

		&:hover {
			background-color: #f3f4f6;
		}
	}

	th,
	td {
		padding: 10px 14px !important;
		font-size: 14px !important;
	}

	th .name {
		font-size: 13px !important;
		text-transform: none;
		letter-spacing: 0;
	}

	.analytics_count_col {
		text-align: right !important;

		.name {
			text-align: right;
		}
	}
}

.analytics_chart_wrap {
	flex: 0 0 auto;
	width: 100%;
	max-width: 340px;
	min-width: 260px;
}
</style>
